import { useCompanyFeature } from 'utils/useCompanyFeature';
import { Box, Button, Divider, Typography } from '@mui/material';
import InitiativeTagSelector from 'components/@home/@messages/ChatPanel/SendForm/InitiativeTagSelector';
import TextField from 'components/formik/TextField';
import { Field, Form } from 'formik';
import { arrayOf, bool, func, object } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import attachmentShape from 'shapes/attachment';
import messageShape from 'shapes/message';
import { I18n } from 'utils/i18n';
import canTagInitiative from 'utils/canTagInitiative';
import { isActiveOmmnioSignProvider } from 'utils/eSignatureProviderAccess';
import { useListDocumentFormatsQuery } from 'store/app/entities/SharedFilesSlice';
import DropzoneBox from '../DropzoneBox';
import Actions from './Actions';
import connector from './connector';
import DiscardButton from './DiscardButton';
import DraftButton from './DraftButton';
import formik from './formik';
import FieldToolbar from './FieldToolbar';
import PdfPreview from './PdfPreview';
import PublishButton from './PublishButton';
import filterDocumentFormatsForChatComposer from './filterDocumentFormatsForChatComposer';
import TemplateSelector from './TemplateSelector';
import Title from './Title';

const getTemplateId = template => template?.uuid || template?.id || template?._id || null;
const getDocumentFormatSendAttachment = template =>
  template?.documentSource === 'fixed' ? template?.templateAttachment || null : null;

const AcknowledgementPreviewForm = ({
  tempAttachment = null,
  message = null,
  onClose,
  setFieldValue,
  submitForm,
  actions,
  values,
  channel = null,
  channelMembers = [],
  onDrop,
  loading,
  error,
  authCompany = null,
  isCompanyAdmin = false,
  digitalSignatureMode = false,
}) => {
  const areInitiativesEnabled = useCompanyFeature('initiatives');
  const canAttachInitiative = canTagInitiative(channel, isCompanyAdmin);
  const [fields, setFields] = useState([]);
  const [currentSignerIndex, setCurrentSignerIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showMessageField, setShowMessageField] = useState(() =>
    Boolean((values.body || '').trim()),
  );
  const isDigitalSignature = values.digitalSignature?.[0] === 'on';
  const loadDocumentFormatTemplates = isDigitalSignature && isActiveOmmnioSignProvider(authCompany);

  const {
    data: { documentFormats = [] } = {},
    isLoading: documentFormatsLoading,
    error: documentFormatsError,
  } = useListDocumentFormatsQuery(undefined, {
    skip: !loadDocumentFormatTemplates,
  });
  const digitalSignatureTemplates = useMemo(() => {
    if (!loadDocumentFormatTemplates) return [];
    return documentFormats
      .filter(format => format.type !== 'xml')
      .filter(f =>
        filterDocumentFormatsForChatComposer(f, { isDirectChannel: !!channel?.isDirect }),
      );
  }, [documentFormats, loadDocumentFormatTemplates, channel?.isDirect]);
  const digitalSignatureTemplatesLoading = loadDocumentFormatTemplates
    ? documentFormatsLoading
    : false;
  const digitalSignatureTemplatesError = loadDocumentFormatTemplates
    ? documentFormatsError
    : undefined;

  // Sync local fields state with Formik values.fields (important for editing existing messages)
  useEffect(() => {
    if ((values.body || '').trim()) {
      setShowMessageField(true);
    }
  }, [values.body]);

  useEffect(() => {
    if (values.fields && values.fields.length > 0 && fields.length === 0) {
      setFields(values.fields);
    }
  }, [values.fields, fields.length]);

  useEffect(() => {
    if (tempAttachment) {
      setFieldValue('attachment', tempAttachment, false); // false = don't validate
    }
  }, [tempAttachment, setFieldValue]);

  useEffect(() => {
    if (!isDigitalSignature) return;
    const attachment = values.attachment || tempAttachment;
    const attachmentName = attachment?.name;
    if (attachmentName && !(values.title || '').trim()) {
      setFieldValue('title', attachmentName, false);
    }
  }, [isDigitalSignature, tempAttachment, values.attachment, values.title, setFieldValue]);

  const saveAsDraft = async e => {
    setFieldValue('isDraft', true);
    await Promise.resolve();
    submitForm(e);
    onClose();
  };

  const discard = () => {
    if (message) actions.messages.destroy(message._id);
    onClose();
  };

  const handleTemplateSelect = template => {
    setSelectedTemplate(template);
    if (loadDocumentFormatTemplates) {
      const templateAttachment = getDocumentFormatSendAttachment(template);
      setFieldValue('template', null);
      setFieldValue('documentFormatTemplate', template || null);
      setFieldValue('fields', template?.fields || []);
      setFields(template?.fields || []);
      setFieldValue('signingMode', template?.signingMode || 'onePerMember');
      setFieldValue('attachment', templateAttachment);
    } else {
      setFieldValue('documentFormatTemplate', null);
      setFieldValue('template', template);
      // Clear attachment if template is selected
      setFieldValue('attachment', null);
    }
    // Also clear temp attachment from redux
    if (actions.drawers?.acknowledgeCreate?.saveTemporarytempAttachment) {
      actions.drawers.acknowledgeCreate.saveTemporarytempAttachment(null);
    }
  };

  const getPdf = () => {
    if (message && message.attachments && message.attachments.length > 0) {
      return message.attachments[0].httpLink;
    }
    // Prefer the formik value over tempAttachment
    const attachment = values.attachment || tempAttachment;
    return attachment?.httpLink;
  };

  const handleAddField = field => {
    const newField = {
      ...field,
      mandatory: true,
      editable: true,
      isYesNo: false,
      signerIndex: values.signingMode === 'oneForAll' ? currentSignerIndex : 0,
      // Match FieldConfigDialog defaults for dynamic prefill
      ...(field.type === 'date' && field.prefillValue == null
        ? { prefillValue: { field: 'currentDate' } }
        : {}),
    };
    const newFields = [...fields, newField];
    setFields(newFields);
    setFieldValue('fields', newFields);
  };

  const handleUpdateField = (fieldId, updates) => {
    const newFields = fields.map(field =>
      field.id === fieldId
        ? {
            ...field,
            ...updates,
            // Signature fields must always be mandatory
            mandatory: field.type === 'signature' ? true : (updates.mandatory ?? field.mandatory),
          }
        : field,
    );
    setFields(newFields);
    setFieldValue('fields', newFields);
  };

  const handleDeleteField = fieldId => {
    const newFields = fields.filter(field => field.id !== fieldId);
    setFields(newFields);
    setFieldValue('fields', newFields);
  };

  const handleSignerChange = signerIndex => {
    setCurrentSignerIndex(signerIndex);
  };

  const handleSigningModeChange = signingMode => {
    setFieldValue('signingMode', signingMode);
    // Reset fields when changing mode to avoid mixing signers
    setFieldValue('fields', []);
    setFields([]);
  };

  const hasAttachment = values.attachment || tempAttachment;

  // Validation: Digital signature requires at least one mandatory signature field
  // For oneForAll mode, each signer must have at least one mandatory signature field
  const hasMandatorySignatureField = (() => {
    if (!isDigitalSignature) return true;

    const mandatorySignatureFields = fields.filter(
      field => field.type === 'signature' && field.mandatory === true,
    );

    if (values.signingMode === 'oneForAll') {
      // Each signer must have at least one mandatory signature field
      const numSigners = channelMembers?.length || 0;
      const signersWithFields = new Set(mandatorySignatureFields.map(field => field.signerIndex));
      return signersWithFields.size === numSigners;
    }

    // For other modes, just need at least one mandatory signature field
    return mandatorySignatureFields.length > 0;
  })();

  const isDigitalSignatureValid =
    !isDigitalSignature || !!selectedTemplate || hasMandatorySignatureField;
  const selectedTemplateAttachment = getDocumentFormatSendAttachment(selectedTemplate);
  const selectedTemplateHasProviderId =
    !loadDocumentFormatTemplates && !!getTemplateId(selectedTemplate);
  const selectedTemplateProvidesDocument =
    !!selectedTemplateAttachment || selectedTemplateHasProviderId;
  const selectedDocumentFormatNeedsUpload =
    loadDocumentFormatTemplates && selectedTemplate && selectedTemplate.documentSource !== 'fixed';
  const shouldShowUpload = !selectedTemplate || selectedDocumentFormatNeedsUpload;
  const pdfSrc = getPdf();
  const canPublish =
    (!!hasAttachment || selectedTemplateProvidesDocument) && isDigitalSignatureValid;

  const handleDrop = acceptedFiles => {
    if (!acceptedFiles?.length) return;
    const file = acceptedFiles[0];
    const isPdf =
      file.type === 'application/pdf' || (file.name && file.name.toLowerCase().endsWith('.pdf'));
    const shouldSanitize = (digitalSignatureMode || values.digitalSignature?.[0] === 'on') && isPdf;
    onDrop(acceptedFiles, {
      sanitizePdf: shouldSanitize,
    });
  };

  return (
    <Form
      style={{
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'scroll',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h5" component="h1" sx={{ marginBottom: 2, fontWeight: 600 }}>
          {isDigitalSignature
            ? I18n.t('AcknowledgementCreate.Add digital signature message')
            : I18n.t('AcknowledgementCreate.Add acknowledge message')}
        </Typography>
        <Title>
          {isDigitalSignature
            ? I18n.t('AcknowledgementCreate.digitalSignatureCardTitleLabel')
            : I18n.t('AcknowledgementCreate.Acknowledge message title')}
        </Title>
        <Field
          component={TextField}
          sx={{ margin: '0 0 25px', width: '100%' }}
          name="title"
          placeholder={
            isDigitalSignature
              ? I18n.t('AcknowledgementCreate.digitalSignatureCardTitlePlaceholder')
              : I18n.t('AcknowledgementCreate.Acknowledge document title')
          }
        />
        {isDigitalSignature &&
          (showMessageField ? (
            <>
              <Title>{I18n.t('AcknowledgementCreate.messageToEmployeeLabel')}</Title>
              <Field
                component={TextField}
                sx={{ margin: '0 0 25px', width: '100%' }}
                name="body"
                multiline
                minRows={3}
                maxRows={8}
                placeholder={I18n.t('AcknowledgementCreate.messageToEmployeePlaceholder')}
              />
            </>
          ) : (
            <Button
              variant="text"
              size="small"
              onClick={() => setShowMessageField(true)}
              sx={{ alignSelf: 'flex-start', mb: 2, textTransform: 'none', px: 0, minWidth: 0 }}
            >
              {I18n.t('AcknowledgementCreate.addMessageLink')}
            </Button>
          ))}

        {/* Show document selection/upload area */}
        {/* Hide only when PDF is uploaded and visible */}
        {!pdfSrc && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }} variant="subtitle2">
              {I18n.t('AcknowledgementCreate.Document')}
            </Typography>
            <DropzoneBox
              onDrop={handleDrop}
              error={error}
              loading={loading}
              showUpload={shouldShowUpload}
            >
              {isDigitalSignature && loadDocumentFormatTemplates && (
                <TemplateSelector
                  onTemplateSelect={handleTemplateSelect}
                  selectedTemplate={selectedTemplate}
                  templates={digitalSignatureTemplates}
                  isLoading={digitalSignatureTemplatesLoading}
                  error={digitalSignatureTemplatesError}
                  requiresUpload={!!selectedDocumentFormatNeedsUpload}
                  providesDocument={!!selectedTemplateProvidesDocument}
                />
              )}
            </DropzoneBox>
          </Box>
        )}

        {/* PDF Preview with signing tools - Only shown when document is uploaded */}
        {pdfSrc && (
          <PdfPreview
            src={pdfSrc}
            fields={fields}
            onAddField={handleAddField}
            onUpdateField={handleUpdateField}
            onDeleteField={handleDeleteField}
            renderToolbar={
              isDigitalSignature ? toolbarProps => <FieldToolbar {...toolbarProps} /> : null
            }
            signingMode={values.signingMode}
            onSigningModeChange={handleSigningModeChange}
            showSigningMode={isDigitalSignature}
            showSignerSelector={isDigitalSignature && values.signingMode === 'oneForAll'}
            currentSignerIndex={values.signingMode === 'oneForAll' ? currentSignerIndex : 0}
            onSignerChange={handleSignerChange}
            members={channelMembers}
          />
        )}
      </Box>
      <Divider light />
      <Actions>
        <DiscardButton type="reset" onClick={discard} sx={{ marginRight: 'auto' }} />
        {areInitiativesEnabled && canAttachInitiative ? (
          <InitiativeTagSelector
            value={values.initiativeTag}
            onChange={value => setFieldValue('initiativeTag', value)}
          />
        ) : null}
        <DraftButton onClick={saveAsDraft} />
        <PublishButton type="submit" disabled={!canPublish} />
      </Actions>
    </Form>
  );
};

AcknowledgementPreviewForm.propTypes = {
  tempAttachment: attachmentShape,
  message: messageShape,
  onClose: func.isRequired,
  setFieldValue: func.isRequired,
  submitForm: func.isRequired,
  actions: object.isRequired,
  values: object.isRequired,
  channel: object,
  channelMembers: arrayOf(object),
  onDrop: func.isRequired,
  loading: bool.isRequired,
  error: bool.isRequired,
  authCompany: object,
  isCompanyAdmin: bool,
  digitalSignatureMode: bool,
};

export default connector(formik(AcknowledgementPreviewForm));
